import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Calendar, Users, Award } from 'lucide-react';

const CaseStudies = () => {
  const { t, i18n } = useTranslation();

  const getCaseStudies = () => {
    const currentLang = i18n.language;
    
    return [
      {
        id: 1,
        title: currentLang === 'fr' ? "E-commerce Platform" : "E-commerce Platform",
        subtitle: currentLang === 'fr' ? "Boutique en ligne haute performance" : "High-performance online store",
        description: currentLang === 'fr' 
          ? "Développement d'une plateforme e-commerce complète avec Shopify Plus, intégration de paiements multiples et automatisation des stocks."
          : "Development of a complete e-commerce platform with Shopify Plus, multiple payment integration and inventory automation.",
        image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
        tags: ["Shopify", "E-commerce", "Payment Integration"],
        metrics: {
          performance: currentLang === 'fr' ? "+300% ventes" : "+300% sales",
          timeline: currentLang === 'fr' ? "8 semaines" : "8 weeks",
          satisfaction: currentLang === 'fr' ? "98% client" : "98% client"
        },
        category: "E-commerce"
      },
      {
        id: 2,
        title: currentLang === 'fr' ? "AI Automation Suite" : "AI Automation Suite",
        subtitle: currentLang === 'fr' ? "Automatisation intelligente des processus" : "Intelligent process automation",
        description: currentLang === 'fr'
          ? "Mise en place d'une suite d'automatisation avec n8n et IA pour optimiser les workflows métier et réduire les tâches répétitives de 80%."
          : "Implementation of an automation suite with n8n and AI to optimize business workflows and reduce repetitive tasks by 80%.",
        image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2232&q=80",
        tags: ["n8n", "AI", "Automation"],
        metrics: {
          performance: currentLang === 'fr' ? "-80% tâches manuelles" : "-80% manual tasks",
          timeline: currentLang === 'fr' ? "12 semaines" : "12 weeks",
          satisfaction: currentLang === 'fr' ? "95% équipe" : "95% team"
        },
        category: "Automation"
      },
      {
        id: 3,
        title: currentLang === 'fr' ? "Mobile Banking App" : "Mobile Banking App",
        subtitle: currentLang === 'fr' ? "Application bancaire mobile sécurisée" : "Secure mobile banking application",
        description: currentLang === 'fr'
          ? "Développement d'une application bancaire mobile avec Flutter, intégrant biométrie, paiements contactless et gestion de portefeuille."
          : "Development of a mobile banking application with Flutter, integrating biometrics, contactless payments and wallet management.",
        image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80",
        tags: ["Flutter", "Mobile", "Security"],
        metrics: {
          performance: currentLang === 'fr' ? "1M+ téléchargements" : "1M+ downloads",
          timeline: currentLang === 'fr' ? "16 semaines" : "16 weeks",
          satisfaction: "4.8★ store"
        },
        category: currentLang === 'fr' ? "Mobile Development" : "Mobile Development"
      }
    ];
  };

  return (
    <section id="case-studies" className="section-spacing relative bg-gradient-to-b from-dark-900 via-dark-800/50 to-dark-900">
      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-gradient">{t('caseStudies.header')}</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('caseStudies.description')}
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {getCaseStudies().map((study, index) => (
            <div
              key={study.id}
              className="card-glass card-hover overflow-hidden group"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={study.image}
                  alt={study.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-secondary-500/20 text-secondary-400 px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm border border-secondary-500/30">
                    {study.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2 text-white group-hover:text-gradient transition-colors duration-300">
                  {study.title}
                </h3>
                <p className="text-secondary-400 font-medium mb-3">
                  {study.subtitle}
                </p>
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  {study.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {study.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="bg-primary-500/10 text-primary-400 px-2 py-1 rounded text-xs font-medium border border-primary-500/20"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-1 gap-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <Award className="w-4 h-4 text-secondary-400" />
                    <span className="text-sm text-gray-300">{study.metrics.performance}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-4 h-4 text-secondary-400" />
                    <span className="text-sm text-gray-300">{study.metrics.timeline}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Users className="w-4 h-4 text-secondary-400" />
                    <span className="text-sm text-gray-300">{study.metrics.satisfaction}</span>
                  </div>
                </div>

                {/* CTA */}
                <button className="w-full flex items-center justify-center space-x-2 text-secondary-400 hover:text-secondary-300 transition-colors duration-300 group/btn">
                  <span className="text-sm font-medium">{t('caseStudies.viewMore')}</span>
                  <ExternalLink className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-secondary-500/10 to-primary-500/10 border border-secondary-500/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-4 text-gradient">
              {t('caseStudies.cta')}
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              {t('caseStudies.ctaDescription')}
            </p>
            <button 
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary"
            >
              {t('caseStudies.ctaButton')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;